import React, { useState, useEffect } from 'react';
import { 
    Form, 
    Input, 
    Modal,
    Button, 
    Space, 
    message, 
    Card, 
    Row, 
    Col,
    Select,
    Alert,
    Typography
} from 'antd';
import { 
    SaveOutlined,
    ReloadOutlined 
} from '@ant-design/icons';
import SwitchShop from '../../shop/switch-shop';
import { getCurrentShopId } from '../../shop/get-current-shop';

const { Title, Text } = Typography;

const NotificationConfig = () => {
    const [form] = Form.useForm();
    const orderCompletedTemplate = Form.useWatch('orderCompletedTemplate', form);
    const orderCompletedSign = Form.useWatch('orderCompletedSign', form);
    const orderCreatedSign = Form.useWatch('orderCreatedSign', form);
    const orderCreatedTemplate = Form.useWatch('orderCreatedTemplate', form);

    const [templates, setTemplates] = useState([]);
    const [signs, setSigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSmsConfig, setHasSmsConfig] = useState(false);

    useEffect(() => {
        fetchSmsConfig();
        fetchSmsTemplates();
        fetchSmsSigns();
    }, []);

    useEffect(() => {
        if(hasSmsConfig) fetchNotifyConfig();
    }, [hasSmsConfig]);

    const fetchSmsConfig = async () => {
    
        try {
            const response = await fetch(`/api/promotion/sms/config/get`);
            const result = await response.json();

            if (result.success) {
                setHasSmsConfig(true);
            } else {
                // message.error(result.data || '获取短信配置失败');
            }
        } catch (error) {
            // console.error('获取短信配置失败:', error);
            // message.error('获取短信配置失败');
        }
    };

    // 获取短信模板
    const fetchSmsTemplates = async () => {
        try {
            const response = await fetch('/api/promotion/sms/template/list');
            const result = await response.json();

            if(result.success && result?.data?.code === 'OK') {
                let data = result.data?.smsTemplateList || [];
                data = data.filter(t => t.auditStatus === 'AUDIT_STATE_PASS' && !(/\${.*?}/.test(t.templateContent)));
                setTemplates(data);
            } else {
                message.error(result.message || '获取短信模板失败');
            }
        } catch (error) {
            console.error('获取短信模板失败:', error);
            message.error('获取短信模板失败');
        }
    };

    // 获取短信签名
    const fetchSmsSigns = async () => {
        try {
            const response = await fetch('/api/promotion/sms/sign/list');
            const result = await response.json();

            if(result.success && result?.data?.code === 'OK') {
                const data = result.data?.smsSignList || [];
                setSigns(data);
            } else {
                message.error(result.message || '获取短信签名失败');
            }
        } catch (error) {
            console.error('获取短信签名失败:', error);
            message.error('获取短信签名失败');
        }
    };

    // 获取当前通知配置
    const fetchNotifyConfig = async () => {
        setLoading(true);
        try {
            const shopId = await getCurrentShopId();
            const response = await fetch(`/api/promotion/sms/notify-config/get?shopId=${shopId}`);
            const result = await response.json();

            if (result.success) {
                // 设置表单初始值
                form.setFieldsValue({
                    orderCreatedTemplate: result.data.orderCreatedTemplate || '',
                    orderCreatedSign: result.data.orderCreatedSign || '',
                    orderCompletedTemplate: result.data.orderCompletedTemplate || '',
                    orderCompletedSign: result.data.orderCompletedSign || '',
                });
            } else {
                message.error(result.message || '获取短信通知配置失败');
            }
        } catch (error) {
            console.error('获取短信通知配置失败:', error);
            message.error('获取短信通知配置失败');
        } finally {
            setLoading(false);
        }
    };

    // 保存配置
    const onSaveConfig = async () => {
        try {
            const values = await form.validateFields();
            const shopId = await getCurrentShopId();

            const configData = {
                shopId,
                orderCreatedTemplate: values.orderCreatedTemplate || '',
                orderCreatedSign: values.orderCreatedSign || '',
                orderCompletedTemplate: values.orderCompletedTemplate || '',
                orderCompletedSign: values.orderCompletedSign || '',
            };

            const response = await fetch(`/api/promotion/sms/notify-config/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(configData),
            });

            const result = await response.json();

            if (result.success) {
                message.success('短信通知配置保存成功');
            } else {
                message.error(result.message || '短信通知配置保存失败');
            }
        } catch (error) {
            console.error('保存短信通知配置失败:', error);
            message.error('保存短信通知配置失败');
        }
    };

    // 重置表单
    const onReset = () => {
        form.resetFields();
        fetchSmsConfig();
    };

    // 模板选择器
    const templateSelector = (name) => (
        <Select
            placeholder="选择短信模板"
            name={name}
            id={name}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => 
                option.children.toLowerCase().includes(input.toLowerCase())
            }
        >
            {templates.map(template => (
                <Select.Option 
                    key={template.templateCode} 
                    value={template.templateCode}
                >
                    {template.templateName} - {template.templateContent}
                </Select.Option>
            ))}
        </Select>
    );

    // 签名选择器
    const signSelector = (name) => (
        <Select
            placeholder="选择短信签名"
            allowClear
        >
            {signs.map(sign => (
                <Select.Option 
                    key={sign.signName} 
                    value={sign.signName}
                >
                    {sign.signName}
                </Select.Option>
            ))}
        </Select>
    );

    return (
        <div>
            <SwitchShop />
            <h2>短信通知配置</h2>
            
            <Alert
                message="配置订单相关事件的短信通知模板"
                description="您可以为订单创建和订单完成设置不同的短信模板和签名"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />
            
            {hasSmsConfig ? <Form
                form={form}
                layout="vertical"
                initialValues={{
                    orderCreatedTemplate: '',
                    orderCreatedSign: '',
                    orderCompletedTemplate: '',
                    orderCompletedSign: '',
                }}
            >
                <Card 
                    title="订单创建通知" 
                    style={{ marginBottom: 24 }}
                    headStyle={{ fontSize: '16px' }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="短信模板"
                                name="orderCreatedTemplate"
                            >
                                {templateSelector('orderCreatedTemplate')}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="短信签名"
                                name="orderCreatedSign"
                            >
                                {signSelector('orderCreatedSign')}
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Form.Item
                        label="短信预览"
                    >
                        <div 
                            style={{ 
                                border: '1px solid #d9d9d9', 
                                padding: '8px 12px', 
                                backgroundColor: '#fafafa',
                                borderRadius: '4px',
                                minHeight: '40px'
                            }}
                        >
                            {(() => {
                                const templateCode = orderCreatedTemplate;
                                const signName = orderCreatedSign;
                                
                                if (!templateCode || !signName) {
                                    return <Text type="secondary">请选择短信模板和签名</Text>;
                                }
                                
                                const template = templates.find(t => t.templateCode === templateCode);
                                
                                return (
                                    <Text>
                                        {signName && `【${signName}】`}
                                        {template ? template.templateContent : '模板未找到'}
                                    </Text>
                                );
                            })()}
                        </div>
                    </Form.Item>
                </Card>

                <Card 
                    title="订单服务完成通知" 
                    style={{ marginBottom: 24 }}
                    headStyle={{ fontSize: '16px' }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="短信模板"
                                name="orderCompletedTemplate"
                            >
                                {templateSelector('orderCompletedTemplate')}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="短信签名"
                                name="orderCompletedSign"
                            >
                                {signSelector('orderCompletedSign')}
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Form.Item
                        label="短信预览"
                    >
                        <div 
                            style={{ 
                                border: '1px solid #d9d9d9', 
                                padding: '8px 12px', 
                                backgroundColor: '#fafafa',
                                borderRadius: '4px',
                                minHeight: '40px'
                            }}
                        >
                            {(() => {
                                const templateCode = orderCompletedTemplate;
                                const signName = orderCompletedSign;
                                
                                if (!templateCode || !signName) {
                                    return <Text type="secondary">请选择短信模板和签名</Text>;
                                }
                                
                                const template = templates.find(t => t.templateCode === templateCode);
                                
                                return (
                                    <Text>
                                        {signName && `【${signName}】`}
                                        {template ? template.templateContent : '模板未找到'}
                                    </Text>
                                );
                            })()}
                        </div>
                    </Form.Item>
                </Card>

                <Form.Item>
                    <Space>
                        <Button 
                            type="primary" 
                            onClick={onSaveConfig}
                            loading={loading}
                            icon={<SaveOutlined />}
                        >
                            保存配置
                        </Button>
                        <Button 
                            onClick={onReset}
                            icon={<ReloadOutlined />}
                        >
                            重置
                        </Button>
                        <Button href='/promotion/sms'>
                            返回
                        </Button>
                    </Space>
                </Form.Item>
            </Form> : <Alert message={<span>请先配置短信发送通道：<a href='/promotion/sms/config'>前往配置</a></span>} type="warning" showIcon /> }
        </div>
    );
};

export default NotificationConfig;